import { getInitials } from '@/lib/avatar';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Play, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { ButtonColorful } from '@/components/ui/button-colorful';
import { CARD_HOVER, DISPLAY_TITLE, EYEBROW } from './landing-styles';
import { useLandingAvatars, useLandingMe } from './useLandingMe';
import { isAllowedEmbedUrl } from './BlockRenderer';

/**
 * Bloques dinámicos del People Landing (Fase 4.4). El contenido sale de
 * peopleLanding.getMe; People solo elige dónde van y el título. Mientras carga
 * getMe se muestra un skeleton del mismo tamaño para que la grilla no salte.
 */

const AVATAR_BG = ['#F3EEFF', '#E5F9F7', '#EBF7FF', '#FEF1EC'];
const NAME_PARTICLES = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'e', 'da', 'do', 'dos', 'das', 'van', 'von']);
/** El community map trae los nombres en MAYÚSCULAS: "FRANCO EMANUEL SALDAÑO" → "Franco Emanuel Saldaño". */
function displayName(raw: string): string {
  return raw
    .trim()
    .split(/\s+/)
    .map((w, i) => {
      const lower = w.toLocaleLowerCase('es');
      if (i > 0 && NAME_PARTICLES.has(lower)) return lower;
      return lower.replace(/(^|[-'])(\p{L})/gu, (_, sep, ch) => sep + ch.toLocaleUpperCase('es'));
    })
    .join(' ');
}

export function Avatar({ name, url, size = 44, idx = 0 }: { name: string; url?: string | null; size?: number; idx?: number }) {
  return url ? (
    <img src={url} alt={name} width={size} height={size} className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />
  ) : (
    <div
      className="rounded-full flex items-center justify-center shrink-0 text-brand-dark font-semibold border-2 border-white"
      style={{ width: size, height: size, background: AVATAR_BG[idx % AVATAR_BG.length], fontSize: Math.max(11, size / 3) }}
      aria-hidden
    >
      {getInitials(name)}
    </div>
  );
}

function CardShell({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('h-full p-6 flex flex-col gap-4', CARD_HOVER, 'hover:translate-y-0 hover:border-border hover:shadow-none', className)}>{children}</div>;
}

function CardSkeleton() {
  return (
    <div className="h-full min-h-[200px] rounded-2xl border border-border bg-card p-6 space-y-3">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-7 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
    </div>
  );
}

/**
 * Compañeros visibles por página del carrusel: los que entran en el ancho de la
 * card (Sol 18/09: "el que alcanza va, si no con la flechita"), mínimo 2. Cada
 * compañero ocupa ~190 px (foto + nombre + rol truncados).
 */
const TEAM_MIN_PER_PAGE = 2;
const TEAM_ITEM_PX = 190;
function useTeamPerPage() {
  const ref = useRef<HTMLDivElement>(null);
  const [perPage, setPerPage] = useState(TEAM_MIN_PER_PAGE);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setPerPage(Math.max(TEAM_MIN_PER_PAGE, Math.floor(el.clientWidth / TEAM_ITEM_PX)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, perPage };
}

/**
 * "Tu equipo": para todos (community map). Líder con foto arriba; debajo, TODO
 * el equipo (Sol 18/09: "dice 'your team' y solo aparece el líder") de a dos,
 * con flechas para recorrer al resto. La fila del buddy solo en onboarding.
 */
export function TeamBlock({ title }: { title?: string }) {
  const { t } = useTranslation();
  const { data: me, isLoading } = useLandingMe();
  const [page, setPage] = useState(0);
  const ob = me?.onboarding;
  const team = me?.team;
  const leader = ob?.leader ?? team?.leader ?? null;
  const teamName = ob?.team || team?.name || '';
  const clapbase = ob?.clapbase || team?.clapbase || '';
  const area = ob?.area || team?.area || '';
  const members = team?.members ?? [];
  const { ref: teamRef, perPage } = useTeamPerPage();
  const pages = Math.max(1, Math.ceil(members.length / perPage));
  const current = Math.min(page, pages - 1);
  const visible = members.slice(current * perPage, current * perPage + perPage);
  // Fotos del líder + los primeros compañeros (el hook cap en 20); el resto, iniciales.
  const { data: avatars } = useLandingAvatars([...(leader ? [leader.email] : []), ...members.map((m) => m.email)]);
  if (isLoading) return <CardSkeleton />;
  if (!ob && !team) return null;
  return (
    <CardShell>
      <span className={EYEBROW}>{title || t('people_landing.dynamic.team_title', 'Tu equipo')}</span>
      <div className="flex flex-col gap-3">
        {leader ? (
          <div className="flex items-center gap-3">
            <Avatar name={leader.name} url={avatars?.[leader.email.toLowerCase()]} idx={0} />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold truncate">{displayName(leader.name)}</span>
              <span className="text-xs text-muted-foreground truncate">
                {t('people_landing.dynamic.your_leader', 'Tu líder')}
                {teamName ? ` · ${teamName}` : ''}
              </span>
            </div>
          </div>
        ) : (
          teamName && <p className="text-sm font-semibold">{teamName}</p>
        )}
        {members.length > 0 && (
          <div ref={teamRef} className="flex flex-col gap-2 border-t border-border/60 pt-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {t('people_landing.dynamic.teammates', { count: members.length, defaultValue_one: '{{count}} compañero', defaultValue_other: '{{count}} compañeros' })}
              </span>
              {pages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={t('people_landing.dynamic.team_prev', 'Anteriores')}
                    disabled={current === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 disabled:opacity-30 disabled:hover:border-border transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-[11px] tabular-nums text-muted-foreground" aria-live="polite">{current + 1}/{pages}</span>
                  <button
                    type="button"
                    aria-label={t('people_landing.dynamic.team_next', 'Siguientes')}
                    disabled={current >= pages - 1}
                    onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                    className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 disabled:opacity-30 disabled:hover:border-border transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${perPage}, minmax(0, 1fr))` }}>
              {visible.map((m, i) => (
                <div key={m.email} className="flex items-center gap-2.5 min-w-0">
                  <Avatar name={m.name} url={avatars?.[m.email]} size={36} idx={current * perPage + i + 1} />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium truncate">{displayName(m.name)}</span>
                    {m.role && <span className="text-xs text-muted-foreground truncate">{m.role}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {ob && (
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0 bg-[#E5F9F7] text-brand-green">
              <UserRound className="h-5 w-5" />
            </div>
            <p className="text-xs text-muted-foreground text-pretty">
              {t(
                'people_landing.dynamic.buddy_note',
                'Tu líder te va a presentar a tu buddy: una persona del equipo que te acompaña las primeras semanas.'
              )}
            </p>
          </div>
        )}
      </div>
      {(area || clapbase) && (
        <p className="mt-auto text-xs text-muted-foreground">{[area, clapbase].filter(Boolean).join(' · ')}</p>
      )}
    </CardShell>
  );
}

/** "Tu primer mes": barra de progreso + próximo paso + checklist. */
export function ProgressBlock({ title }: { title?: string }) {
  const { t } = useTranslation();
  const { data: me, isLoading } = useLandingMe();
  if (isLoading) return <CardSkeleton />;
  const ob = me?.onboarding;
  if (!ob) return null;
  const day = Math.max(0, ob.day);
  const pct = Math.min(100, Math.round((day / 30) * 100));
  const week = Math.min(5, Math.max(1, Math.ceil(Math.max(1, day) / 7)));
  return (
    <CardShell>
      <span className={EYEBROW}>{title || t('people_landing.dynamic.progress_title', 'Tu primer mes')}</span>
      <div className="flex items-baseline justify-between">
        <span className={cn(DISPLAY_TITLE, 'text-2xl')}>
          {ob.day <= 0
            ? t('people_landing.dynamic.starts_soon', 'Arrancás en {{n}} días', { n: 1 - ob.day })
            : t('people_landing.dynamic.week_n', 'Semana {{n}}', { n: week })}
        </span>
        <span className="text-sm text-muted-foreground">{t('people_landing.dynamic.day_of', 'Día {{day}} de 30', { day })}</span>
      </div>
      <div className="h-1.5 rounded-full bg-border overflow-hidden">
        <div className="h-full rounded-full bg-brand-green transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      {ob.nextStep && (
        <p className="text-sm">
          <span className="text-muted-foreground">{t('people_landing.dynamic.next', 'Próximo')}: </span>
          {ob.nextStep.label}
        </p>
      )}
      <div className="mt-auto flex flex-wrap gap-2">
        {ob.nextStep && <ButtonColorful size="sm" href={ob.nextStep.url} label={ob.nextStep.label} />}
        {ob.checklistUrl && (
          <ButtonColorful size="sm" variant="secondary" href={ob.checklistUrl} label={t('people_landing.dynamic.checklist', 'Tu checklist de onboarding')} />
        )}
      </div>
    </CardShell>
  );
}

/** "Novedades": ingresos del mes + aniversario. Para todos. */
export function NewsBlock({ title }: { title?: string }) {
  const { t } = useTranslation();
  const { data: me, isLoading } = useLandingMe();
  if (isLoading) return <CardSkeleton />;
  const hires = me?.news.newHiresThisMonth ?? [];
  const years = me?.news.anniversaryYears ?? null;
  return (
    <CardShell>
      <span className={EYEBROW}>{title || t('people_landing.dynamic.news_title', 'Novedades')}</span>
      {hires.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <div className="flex">
              {hires.slice(0, 4).map((h, i) => (
                <div key={`${h.firstName}-${i}`} className={cn(i > 0 && '-ml-2')}>
                  <Avatar name={h.firstName} size={32} idx={i} />
                </div>
              ))}
            </div>
            <span className={cn(DISPLAY_TITLE, 'text-xl')}>
              {t('people_landing.dynamic.new_hires', { count: hires.length, defaultValue_one: '{{count}} clapper nuevo este mes', defaultValue_other: '{{count}} clappers nuevos este mes' })}
            </span>
          </div>
          <p className="text-sm text-muted-foreground text-pretty">
            {hires.map((h) => (h.team ? `${h.firstName} (${h.team})` : h.firstName)).join(', ')} ·{' '}
            {t('people_landing.dynamic.welcome_them', 'dales la bienvenida')}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('people_landing.dynamic.no_news', 'Sin novedades este mes.')}</p>
      )}
      {years && (
        <div className="mt-auto rounded-xl bg-[#F3EEFF] dark:bg-[#2a2440] px-4 py-3">
          <p className={cn(DISPLAY_TITLE, 'text-lg')}>
            {t('people_landing.dynamic.anniversary', { count: years, defaultValue_one: 'Este mes cumplís {{count}} año en Winclap', defaultValue_other: 'Este mes cumplís {{count}} años en Winclap' })}
          </p>
          <p className="text-xs text-muted-foreground">{t('people_landing.dynamic.anniversary_sub', 'Gracias por tanto.')}</p>
        </div>
      )}
    </CardShell>
  );
}

/** Video de bienvenida: pieza central para ingresos; botón secundario para el resto. */
export function VideoBlock({ title, url }: { title?: string; url?: string }) {
  const { t } = useTranslation();
  const { data: me, isLoading } = useLandingMe();
  const [open, setOpen] = useState(false);
  if (isLoading) return <CardSkeleton />;
  const src = url || me?.welcomeVideoUrl || '';
  const heading = title || t('people_landing.dynamic.video_title', 'Un mensaje para vos, de parte de todo Winclap');
  const isOnboarding = !!me?.onboarding;
  if (!src || !isAllowedEmbedUrl(src)) return null;

  if (open) {
    return (
      <div className="relative h-full min-h-[280px] overflow-hidden rounded-2xl border border-border bg-black">
        {/* Sin referrerPolicy: YouTube necesita el referrer (error 153). */}
        <iframe
          src={src}
          title={heading}
          className="absolute inset-0 h-full w-full"
          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation allow-forms"
          allowFullScreen
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      </div>
    );
  }

  if (!isOnboarding) {
    return (
      <CardShell>
        <span className={EYEBROW}>{t('people_landing.dynamic.welcome_eyebrow', 'Bienvenida')}</span>
        <h3 className={cn(DISPLAY_TITLE, 'text-2xl')}>{heading}</h3>
        <div className="mt-auto">
          <ButtonColorful variant="secondary" size="sm" hideArrow onClick={() => setOpen(true)}>
            <Play className="h-3.5 w-3.5" />
            {t('people_landing.dynamic.rewatch', 'Volver a ver la bienvenida')}
          </ButtonColorful>
        </div>
      </CardShell>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="group relative block h-full min-h-[280px] w-full overflow-hidden rounded-2xl border border-brand-dark bg-brand-dark text-left"
      aria-label={t('people_landing.dynamic.play', 'Reproducir el video de bienvenida')}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 80% at 30% 30%, rgba(206,171,255,.45) 0%, rgba(17,17,17,0) 70%), radial-gradient(50% 70% at 80% 70%, rgba(28,201,185,.35) 0%, rgba(17,17,17,0) 70%)',
        }}
      />
      <div className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-white/15 backdrop-blur-sm border border-white/30 transition-transform duration-300 group-hover:scale-110">
        <Play className="h-7 w-7 text-white translate-x-0.5" fill="currentColor" />
      </div>
      <div className="absolute inset-x-0 bottom-0 z-10 p-6 flex flex-col gap-1">
        <span className={EYEBROW}>{t('people_landing.dynamic.welcome_eyebrow', 'Bienvenida')}</span>
        <span className={cn(DISPLAY_TITLE, 'text-2xl md:text-3xl text-white leading-[1.1]')}>{heading}</span>
        <span className="text-[13px] text-white/70">{t('people_landing.dynamic.video_hint', 'Video · 2 min')}</span>
      </div>
    </button>
  );
}
